// Card assets for Uno game
// This file contains SVG-based card designs

// Card colors
export const CARD_COLORS = {
  red: '#D50000',
  blue: '#050DF7', 
  green: '#008707ff',
  yellow: '#f2db05',
  black: '#000000',
};

// Card symbols for special cards
export const CardSymbols = {
  Skip: ({ color }) => (
    <svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">
      <text x="50" y="83" fontSize="40" fontWeight="900" fill={color} textAnchor="middle" dominantBaseline="middle">S</text>
    </svg>
  ),
  
  Reverse: ({ color }) => (
    <svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">
      <text x="50" y="83" fontSize="40" fontWeight="900" fill={color} textAnchor="middle" dominantBaseline="middle">R</text>
    </svg>
  ),
  
  'Draw Two': ({ color }) => (
    <svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">
      <text x="50" y="83" fontSize="40" fontWeight="900" fill={color} textAnchor="middle" dominantBaseline="middle">+2</text>
    </svg>
  ),
  
  Wild: () => (
    <svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="75" r="35" fill="none" stroke="#FFFFFF" strokeWidth="3" />
      <path d="M50,40 A35,35 0 0,1 85,75 L50,75 Z" fill="#D50000" />
      <path d="M85,75 A35,35 0 0,1 50,110 L50,75 Z" fill="#2196F3" />
      <path d="M50,110 A35,35 0 0,1 15,75 L50,75 Z" fill="#4CAF50" />
      <path d="M15,75 A35,35 0 0,1 50,40 L50,75 Z" fill="#FFEB3B" />
    </svg>
  ),
  
  'Wild Draw Four': () => (
    <svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="75" r="35" fill="none" stroke="#FFFFFF" strokeWidth="3" />
      <path d="M50,40 A35,35 0 0,1 85,75 L50,75 Z" fill="#D50000" />
      <path d="M85,75 A35,35 0 0,1 50,110 L50,75 Z" fill="#2196F3" />
      <path d="M50,110 A35,35 0 0,1 15,75 L50,75 Z" fill="#4CAF50" />
      <path d="M15,75 A35,35 0 0,1 50,40 L50,75 Z" fill="#FFEB3B" />
      <text x="50" y="85" fontSize="20" fill="#FFFFFF" fontWeight="bold" textAnchor="middle">+4</text>
    </svg>
  ),
};

// Card back design
export const CardBack = ({ position }) => {
  // Check if this is player 1 or 3 to flip UNO text
  const flipText = position === 1 || position === 3;
  
  return (
    <svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">
      {/* Outer black rectangle */}
      <rect width="100" height="150" fill="#000000" rx="10" />
      
      {/* Inner white border */}
      <rect x="5" y="5" width="90" height="140" fill="none" stroke="#FFFFFF" strokeWidth="2" rx="8" />
      
      {/* White UNO text */}
      <text 
        x="50" 
        y="75" 
        fontSize="28" 
        fontWeight="bold" 
        fill="#FFFFFF" 
        textAnchor="middle"
        dominantBaseline="middle"
        transform={flipText ? "rotate(180, 50, 75)" : ""}
      >
        UNO
      </text>
    </svg>
  );
};

// Generate a card face based on color and value
export const CardFace = ({ card, position }) => {
  const { color, value, type } = card;
  const bgColor = color ? CARD_COLORS[color] : CARD_COLORS.black;
  const textColor = '#FFFFFF'; // Always white text
  
  // Check if this is player 1 or 3 to flip text
  const flipText = position === 1 || position === 3;
  
  // Special card symbols
  if (type !== 'number') {
    const SymbolComponent = CardSymbols[value];
    if (SymbolComponent) {
      return (
        <svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">
          <rect width="100" height="150" fill={bgColor} rx="10" />
          {flipText ? (
            <>
              <text x="10" y="30" fontSize={value === 'Wild Draw Four' || value === 'Draw Two' ? "16" : value.length > 8 ? "12" : "20"} fontWeight="900" fill={textColor} transform="rotate(180, 10, 30)" strokeWidth="0.5" stroke={textColor}>
                {value === 'Wild Draw Four' ? 'Draw Four' : value === 'Draw Two' ? 'Draw Two' : value}
              </text>
              <text x="50" y="30" fontSize="14" fontWeight="bold" fill={textColor} textAnchor="middle" transform="rotate(180, 50, 30)">
                {/* Empty - Draw Two now uses the same style as Draw Four */}
              </text>
              <text x="90" y="140" fontSize={value === 'Wild Draw Four' || value === 'Draw Two' ? "16" : value.length > 8 ? "12" : "20"} fontWeight="900" fill={textColor} textAnchor="end" transform="rotate(180, 90, 140)" strokeWidth="0.5" stroke={textColor}>
                {value === 'Wild Draw Four' ? 'Draw Four' : value === 'Draw Two' ? 'Draw Two' : value}
              </text>
              <text x="50" y="140" fontSize="14" fontWeight="bold" fill={textColor} textAnchor="middle" transform="rotate(180, 50, 140)">
                {/* Empty - Draw Two now uses the same style as Draw Four */}
              </text>
              <SymbolComponent color={textColor} />
            </>
          ) : (
            <>
              <text x="10" y="30" fontSize={value === 'Wild Draw Four' || value === 'Draw Two' ? "16" : value.length > 8 ? "12" : "20"} fontWeight="900" fill={textColor} strokeWidth="0.5" stroke={textColor}>
                {value === 'Wild Draw Four' ? 'Draw Four' : value === 'Draw Two' ? 'Draw Two' : value}
              </text>
              <text x="50" y="30" fontSize="14" fontWeight="bold" fill={textColor} textAnchor="middle">
                {/* Empty - Draw Two now uses the same style as Draw Four */}
              </text>
              <text x="90" y="140" fontSize={value === 'Wild Draw Four' || value === 'Draw Two' ? "16" : value.length > 8 ? "12" : "20"} fontWeight="900" fill={textColor} textAnchor="end" strokeWidth="0.5" stroke={textColor}>
                {value === 'Wild Draw Four' ? 'Draw Four' : value === 'Draw Two' ? 'Draw Two' : value}
              </text>
              <text x="50" y="140" fontSize="14" fontWeight="bold" fill={textColor} textAnchor="middle">
                {/* Empty - Draw Two now uses the same style as Draw Four */}
              </text>
              <SymbolComponent color={textColor} />
            </>
          )}
        </svg>
      );
    }
  }
  
  // Number cards
  return (
    <svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">
      <rect width="100" height="150" fill={bgColor} rx="10" />
      <circle cx="50" cy="75" r="30" fill="#FFFFFF" />
      {flipText ? (
        <>
          <text x="10" y="30" fontSize="24" fontWeight="bold" fill={textColor} transform="rotate(180, 10, 30)">{value}</text>
          <text x="90" y="140" fontSize="24" fontWeight="bold" fill={textColor} textAnchor="end" transform="rotate(180, 90, 140)">{value}</text>
          <text x="50" y="85" fontSize="36" fontWeight="bold" fill={bgColor} textAnchor="middle" transform="rotate(180, 50, 85)">{value}</text>
        </>
      ) : (
        <>
          <text x="10" y="30" fontSize="24" fontWeight="bold" fill={textColor}>{value}</text>
          <text x="90" y="140" fontSize="24" fontWeight="bold" fill={textColor} textAnchor="end">{value}</text>
          <text x="50" y="85" fontSize="36" fontWeight="bold" fill={bgColor} textAnchor="middle">{value}</text>
        </>
      )}
    </svg>
  );
};
